import { Autocomplete, Box, FormLabel, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import InvoiceBillingItemsCard from "./billingItems/InvoiceBillingItemsCard";
import BillingItemsMessageError from "./billingItems/BillingItemsMessageError";
import { getWorksSearch } from "../../api/works";


const InvoiceBillingItems = ({ data, setData, onValidityChange, setPartsValue, setWorksValue, confirmedOptions, setConfirmedOptions,
    billingCardHasError, resourceSelected, setResourceSelected, onWorkIdChange, onWorksDetails, loadingWorks, setLoadingWorks, onLoadingChange }) => {
    const [worksOptions, setWorksOptions] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [loadingWorkIds, setLoadingWorkIds] = useState([]);
    const [isAnyCardLoading, setIsAnyCardLoading] = useState(false);

    useEffect(() => {
        if (!data.client?.id) {
            setWorksOptions([]);
            onWorksDetails?.([]);
            return;
        }

        const fetchWorks = async () => {
            setLoadingWorks(true);
            try {
                const params = { rootIds: [data.client.id] };
                if (searchText && searchText.trim() !== "") {
                    params.q = searchText.trim();
                }

                const result = await getWorksSearch(params);
                const worksData = Array.isArray(result?.results) ? result.results : [];

                const options = worksData.map(work => ({
                    label: work.title,
                    work
                }));

                setWorksOptions(options);
                onWorksDetails?.(worksData);
            } catch (err) {
                setWorksOptions([]);
                onWorksDetails?.([]);
            } finally {
                setLoadingWorks(false);
            }
        };

        if (!searchText) {
            fetchWorks();
        } else {

            const timeoutId = setTimeout(fetchWorks, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [data.client, searchText, onWorksDetails]);



    const resourceOptions = useMemo(() => {
        return worksOptions.filter(
            (option) =>
                !confirmedOptions.some(
                    (confirmed) => confirmed.work.id === option.work.id
                )
        );
    }, [worksOptions, confirmedOptions]);

    const toNumber = (v) => {
        if (v == null) return 0;
        if (typeof v === "number") return v;
        const normalized = String(v)
            .replace(/[^\d,.,-]/g, "")
        const n = parseFloat(normalized);
        return Number.isFinite(n) ? n : 0;
    };

    const totals = useMemo(() => {
        let parts = 0;
        let services = 0;
        for (const { work } of confirmedOptions) {
            parts += toNumber(work.subtotal_parts_price);
            services += toNumber(work.subtotal_services_price);
        }
        return { parts, services };
    }, [confirmedOptions]);

    useEffect(() => {
        setPartsValue(totals.parts);
        setWorksValue(totals.services);
    }, [totals, setPartsValue, setWorksValue]);

    useEffect(() => {
        const isValid = confirmedOptions.length > 0 && !billingCardHasError && !isAnyCardLoading;
        onValidityChange?.(isValid);
    }, [confirmedOptions, billingCardHasError, onValidityChange]);

    const handleConfirm = async (option) => {
        if (!option) return;
        if (confirmedOptions.some(o => o.work.id === option.work.id)) return;

        setLoadingWorkIds(prev => [...prev, option.work.id]);

        const updated = [...confirmedOptions, option];
        setConfirmedOptions(updated);[]

        const updatedResources = updated.map(o => o.work);
        setData(prev => ({ ...prev, resources: updatedResources }));
        onWorkIdChange?.(updatedResources.map(w => w.id));

        setLoadingWorkIds(prev => prev.filter(id => id !== option.work.id));
        setResourceSelected(null);
    };

    const handleRemove = (workId) => {
        const updated = confirmedOptions.filter(o => o.work.id !== workId);
        setConfirmedOptions(updated);

        const works = updated.map(o => o.work);
        setData(prev => ({ ...prev, resources: works }));

        onWorkIdChange?.(works.map(w => w.id));
    };

    const handleCardLoadingChange = (workId, isLoading) => {
        setLoadingWorkIds(prev => {
            const updated = isLoading
                ? [...new Set([...prev, workId])]
                : prev.filter(id => id !== workId);

            const anyLoading = updated.length > 0;
            setIsAnyCardLoading(anyLoading);
            onLoadingChange?.(anyLoading);
            return updated;
        });
    };

    const worksIds = data.resources?.map(work => work.id) || [];

    const hasError = Array.isArray(confirmedOptions)
        ? confirmedOptions.some(option => option.work.status !== "reviewed") : false;

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ minHeight: '30rem'}}>
                {hasError && (
                    <BillingItemsMessageError />
                )}
                <Box sx={{ p: '3rem 1.5rem', gap: '2.5rem' }}>
                    <FormLabel sx={{ color: 'text.primary' }}><b> Atividade para cobrança</b> <FormLabel sx={{color:'error.main'}}><b>*</b></FormLabel></FormLabel>
                    <Autocomplete
                        disablePortal
                        options={resourceOptions}
                        getOptionLabel={(option) => option?.label || ''}
                        value={resourceSelected || null}
                        inputValue={searchText}
                        onChange={(e, newValue) => {
                            setResourceSelected(newValue);
                            if (newValue) {
                                handleConfirm(newValue);
                                setResourceSelected(null);
                                setSearchText('');
                            }
                        }}
                        onInputChange={(e, newInputValue) => setSearchText(newInputValue)}
                        noOptionsText={!loadingWorks ? "Nenhum resultado encontrado" : "Carregando..."}
                        slotProps={{
                            listbox: {
                                sx: {
                                    maxHeight: '13rem',
                                },
                            },
                        }}
                        disabled={loadingWorkIds.length > 0}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder='Pesquisar'
                            />
                        )}
                    />
                </Box>
                {confirmedOptions.map(option => (
                    <InvoiceBillingItemsCard
                        key={option.work.id}
                        worksDetails={{
                            title: option.label,
                            subtotal_parts_price: option.work.subtotal_parts_price,
                            subtotal_services_price: option.work.subtotal_services_price,
                            id: option.work.id,
                        }}
                        billingItemsCardError={option.work.status !== "reviewed"}
                        onRemove={() => handleRemove(option.work.id)}
                        onLoadingChange={handleCardLoadingChange}
                    />
                ))}
            </Box>
            <Box sx={{ p: '3rem 1.5rem' }}>
                <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b> Indica um campo obrigatório </Typography>
            </Box>
        </Box>
    )
}

export default InvoiceBillingItems; 